import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent, Site } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { angularPlugin } from '@datadog/browser-rum-angular';
import { environment } from '../../../environments/environment';
import {
  DeploymentEnv,
  readOccBackendBaseUrlMeta,
  resolveDeploymentEnv,
  resolveOccBackendBaseUrl,
} from './deployment-env';

const OWNING_TEAM = 'ecom-oneshop';

/**
 * Browser-reported CSP violations for blocked third-party trackers surface
 * as RUM errors even though nothing in the app broke — they dominate error
 * volume and mask real bugs. Same fix as oneshop-ecom (USONESHOP-3643).
 */
export function isCspViolationEvent(event: RumEvent): boolean {
  return event.type === 'error' && !!event.error?.message?.startsWith('csp_violation:');
}

// A credential parameter (`?token=`, `&bearer=`, `#id_token=` ...), tested on the decoded text.
const CREDENTIAL_PAIR = /[?&#](?:token|access_token|id_token|refresh_token|bearer|code|otp|password|secret|sid|key)=/i;
const PERCENT_ESCAPE = /%[0-9a-f]{2}/gi;
const FIRST_QUERY_OR_FRAGMENT = /[?#]/;
/**
 * Undoes the encodings a URL can hide a parameter name behind (`%74oken`, `%26`, `&amp;`, nested encoded URLs).
 * Decodes escape by escape, so one malformed `%ZZ` cannot stop the rest from being decoded.
 */
function decodeForInspection(chunk: string): string {
  let text = chunk.replaceAll('&amp;', '&').replaceAll('\\u0026', '&');
  for (let pass = 0; pass < 3; pass++) {
    const decoded = text
      .replace(PERCENT_ESCAPE, (escape) => String.fromCharCode(Number.parseInt(escape.slice(1), 16)))
      .replaceAll('&amp;', '&');
    if (decoded === text) {
      break;
    }
    text = decoded;
  }
  return text;
}

function hasCredential(text: string): boolean {
  return CREDENTIAL_PAIR.test(decodeForInspection(text));
}

function redactChunk(chunk: string): string {
  if (!hasCredential(chunk)) {
    return chunk;
  }
  const cut = chunk.search(FIRST_QUERY_OR_FRAGMENT);
  // Nothing safe to keep when there is no query to cut at, or when the credential is already in the kept prefix
  // (for example a fully percent-encoded URL such as `https%3A%2F%2Fx%2Flogin%3Ftoken%3Dsecret`).
  if (cut === -1 || hasCredential(chunk.slice(0, cut))) {
    return '[redacted]';
  }
  // Keep the paren that closes a `(url)` frame, but only when this chunk opened it, never part of a value.
  const closer = chunk.startsWith('(') && chunk.endsWith(')') ? ')' : '';
  return `${chunk.slice(0, cut)}?[redacted]${closer}`;
}

/**
 * Password reset, OTP and SSO handoff links carry credentials in the query string or fragment, and
 * `defaultPrivacyLevel` does not cover URLs. Each whitespace-separated chunk that carries a credential parameter (in
 * any encoding) keeps its scheme, host and path and loses everything after, so no guess about where a credential
 * value ends is needed, and nothing after the cut is kept (not even a `:line:col` suffix, which cannot be told apart from
 * credential content). Works for URL fields and for free text such as error messages and stack traces.
 */
export function redactCredentials<T extends string | undefined>(text: T): T {
  return (typeof text === 'string' ? text.replace(/\S+/g, redactChunk) : text) as T;
}

export function containsCredential(text: string | undefined): boolean {
  return typeof text === 'string' && redactCredentials(text) !== text;
}

/**
 * The SDK only applies `beforeSend` edits to a fixed list of fields (view.url/referrer/name, error.message/stack/
 * resource.url, resource.url, action.target.name, long-task script urls, ...). `error.handling_stack` and
 * `error.causes` are not on it, so a credential there cannot be scrubbed and the whole event has to be dropped.
 */
export function hasUnscrubbableCredentials(event: RumEvent): boolean {
  if (event.type !== 'error' || !event.error) {
    return false;
  }
  const { handling_stack, causes } = event.error;
  return (
    containsCredential(handling_stack) ||
    (causes ?? []).some((cause) => containsCredential(cause.message) || containsCredential(cause.stack))
  );
}

/** Mutates the event in place, as the Datadog `beforeSend` contract allows, using only fields the SDK honours. */
export function redactEventUrls(event: RumEvent): void {
  if ('view' in event && event.view) {
    event.view.url = redactCredentials(event.view.url);
    event.view.referrer = redactCredentials(event.view.referrer);
    event.view.name = redactCredentials(event.view.name);
  }
  if ((event.type === 'view' || event.type === 'view_update') && event.view.performance?.lcp?.resource_url) {
    event.view.performance.lcp.resource_url = redactCredentials(event.view.performance.lcp.resource_url);
  }
  if (event.type === 'resource') {
    event.resource.url = redactCredentials(event.resource.url);
  }
  if (event.type === 'action' && event.action?.target) {
    event.action.target.name = redactCredentials(event.action.target.name);
  }
  if (event.type === 'long_task') {
    event.long_task?.scripts?.forEach((script) => {
      script.source_url = redactCredentials(script.source_url);
    });
  }
  if (event.type === 'error' && event.error) {
    event.error.message = redactCredentials(event.error.message);
    event.error.stack = redactCredentials(event.error.stack);
    if (event.error.resource) {
      event.error.resource.url = redactCredentials(event.error.resource.url);
    }
  }
}

/**
 * Initializes Datadog RUM + Logs for this app.
 *
 * OneTrust is loaded in src/index.html (otSDKStub.js + an empty
 * OptanonWrapper callback), but nothing in this app currently uses it to
 * gate any script, so init here runs unconditionally on bootstrap —
 * consistent with oneshop-ecom's own production RUM setup, which also loads
 * OneTrust but doesn't check consent categories before initializing RUM. If
 * that categorization ever changes (e.g. RUM/APM reclassified as requiring
 * opt-in rather than strictly-necessary), this would need to check
 * `window.OnetrustActiveGroups` / hook into `OptanonWrapper` instead.
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogRumService {
  private readonly platformId = inject(PLATFORM_ID);

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // app.config.server.ts merges this same appConfig into the SSR
      // bootstrap; the Datadog Browser SDK is client-only, so skip entirely
      // when running server-side (Copilot review, USONESHOP-3875).
      return;
    }

    const { applicationId, clientToken, site, service } = environment.datadog;

    if (!applicationId || !clientToken) {
      // Placeholders not filled in yet — no-op instead of sending garbage data.
      return;
    }

    const env = this.resolveEnv();
    const ddSite = site as Site;

    datadogRum.init({
      applicationId,
      clientToken,
      site: ddSite,
      service,
      env,
      sessionSampleRate: 100,
      // 5% (was 20%) to reduce Session Replay cost.
      sessionReplaySampleRate: 5,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      beforeSend: (event) => {
        if (isCspViolationEvent(event) || hasUnscrubbableCredentials(event)) {
          return false;
        }
        try {
          redactEventUrls(event);
        } catch {
          // Fail closed: an event that cannot be scrubbed is not sent.
          return false;
        }
        return true;
      },
      // Route patterns as view names instead of resolved URLs.
      plugins: [angularPlugin({ router: true })],
    });

    datadogLogs.init({
      clientToken,
      site: ddSite,
      service,
      env,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });

    // Owning team on every RUM event and log, for filtering and cost attribution in Datadog.
    datadogRum.setGlobalContextProperty('team', OWNING_TEAM);
    datadogLogs.setGlobalContextProperty('team', OWNING_TEAM);
  }

  /**
   * Shares the OCC-host-to-tier resolver with FeatureFlagService (same
   * deploy-injected signal, same tiers) so Datadog's `env` tag lines up with
   * the environment flags are evaluated against, with one set of
   * host-matching rules to maintain.
   */
  private resolveEnv(): string {
    const occUrl = resolveOccBackendBaseUrl(environment.occBaseUrl, readOccBackendBaseUrlMeta());

    return resolveDeploymentEnv(occUrl, (environment.datadog.defaultEnv ?? 'development') as DeploymentEnv);
  }
}
